import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormNode, StaticPreviewRow } from '../designer/types/form.types'
import { ApiService } from '@/utils/api'
import {
  findJournalPreviewTables,
  journalPreviewTableNodes,
} from './formSchemaJournalPreview'
import {
  assignRowsToJournalTables,
  collectAccountIds,
  ecritureLinesToPreviewRows,
  resolveJournalLineSources,
  splitLinesForJournalEcritures,
  type JournalLineSource,
  type MovementLineSource,
} from './journalPreviewData'

export function useJournalPreviewRows(options: {
  connectionId: string
  objectName: string
  recordId: string | null | undefined
  journalGroup: FormNode | null
  /** Fallback when journalGroup missing (stale cfg.forms) — walk full form root. */
  formRoot?: FormNode[]
  documentData: Record<string, unknown>
  linesPartName?: string
  reloadKey?: number
  /** Object/catalog to resolve account labels against (from the Lines part's account_id ref target). */
  chartOfAccountsTarget?: string
}): {
  loading: boolean
  rowsByTable: Record<string, StaticPreviewRow[]>
  hasPostedMovements: boolean
  tableNodes: { name: string }[]
} {
  const {
    connectionId,
    objectName,
    recordId,
    journalGroup,
    formRoot,
    documentData,
    linesPartName = 'Lines',
    reloadKey = 0,
    chartOfAccountsTarget,
  } = options

  const [loading, setLoading] = useState(true)
  const [movements, setMovements] = useState<MovementLineSource[]>([])
  const [hasPostedMovements, setHasPostedMovements] = useState(false)
  const [displayById, setDisplayById] = useState<Map<string, string>>(new Map())

  const lines = useMemo(() => {
    const raw = documentData[linesPartName]
    return Array.isArray(raw) ? (raw as JournalLineSource[]) : []
  }, [documentData, linesPartName])

  const loadMovementsAndLabels = useCallback(async () => {
    setLoading(true)
    let movs: MovementLineSource[] = []
    if (recordId) {
      try {
        const resp = await ApiService.get<{ data: { movements: Record<string, unknown>[] }[] }>(
          `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}/movements`,
        )
        let lineNo = 0
        for (const block of resp.data ?? []) {
          for (const row of block.movements ?? []) {
            lineNo += 1
            movs.push({
              line_number: lineNo,
              account_id: row.account_id as string | undefined,
              debit_total: Number(row.debit_total ?? row.debit ?? 0),
              credit_total: Number(row.credit_total ?? row.credit ?? 0),
            })
          }
        }
      } catch {
        movs = []
      }
    }
    setMovements(movs)
    setHasPostedMovements(movs.length > 0)
    setLoading(false)
  }, [connectionId, objectName, recordId])

  useEffect(() => {
    void loadMovementsAndLabels()
  }, [loadMovementsAndLabels, reloadKey])

  const effectiveLines = useMemo(
    () => resolveJournalLineSources(lines, movements, hasPostedMovements, []),
    [lines, movements, hasPostedMovements],
  )

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const accountIds = collectAccountIds(effectiveLines, movements)
      const map = new Map<string, string>()
      if (accountIds.length && chartOfAccountsTarget) {
        try {
          const lookup = await ApiService.get<{ data: { id: string; display: string }[] }>(
            `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(chartOfAccountsTarget)}/lookup`,
          )
          for (const o of lookup.data ?? []) {
            if (o?.id) map.set(o.id, o.display || o.id)
          }
        } catch {
          /* keep ids */
        }
      }
      if (!cancelled) setDisplayById(map)
    })()
    return () => {
      cancelled = true
    }
  }, [connectionId, effectiveLines, movements, chartOfAccountsTarget])

  const tableNodes = useMemo(() => {
    if (journalGroup) return journalPreviewTableNodes(journalGroup)
    return findJournalPreviewTables(formRoot)
  }, [journalGroup, formRoot])

  const rowsByTable = useMemo(() => {
    if (!journalGroup) return {}
    const [ecritureA, ecritureB] = splitLinesForJournalEcritures(
      effectiveLines,
      movements,
      hasPostedMovements,
    )
    return assignRowsToJournalTables(tableNodes, [
      ecritureLinesToPreviewRows(ecritureA, displayById),
      ecritureLinesToPreviewRows(ecritureB, displayById),
    ])
  }, [journalGroup, tableNodes, effectiveLines, movements, hasPostedMovements, displayById])

  return { loading, rowsByTable, hasPostedMovements, tableNodes }
}
